import { c as _c } from "react/compiler-runtime";
import { COORDINATE_PRECISION, LATITUDE_MAX, LONGITUDE_MAX } from "@utils/geocoding-utils";
import { QueryParams } from "@router/route-params";
import { useCallback, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { parseNumberParam } from "@utils/url-utils";

/** Returns whether the given coordinates and altitude describe a view the map can navigate to. */
function isViewWithinBounds(view) {
  return Math.abs(view.lat) <= LATITUDE_MAX && Math.abs(view.lng) <= LONGITUDE_MAX && view.altitude >= 0;
}

/**
 * Reads the world map camera pose from the URL query params.
 * Returns undefined when any of the required params are missing or out of range.
 * `heading` and `tilt` default to 0 (north-up, top-down) when absent.
 */
export function parseMapViewFromParams(searchParams) {
  const lat = parseNumberParam(searchParams, QueryParams.lat);
  const lng = parseNumberParam(searchParams, QueryParams.lng);
  const altitude = parseNumberParam(searchParams, QueryParams.alt);
  if (lat === null || lng === null || altitude === null) {
    return undefined;
  }
  const heading = parseNumberParam(searchParams, QueryParams.heading) ?? 0;
  const tilt = parseNumberParam(searchParams, QueryParams.tilt) ?? 0;
  const view = {
    lat,
    lng,
    altitude,
    heading,
    tilt
  };
  return isViewWithinBounds(view) ? view : undefined;
}

/**
 * Writes the world map camera pose into the given query params, preserving all other params.
 * Coordinates are rounded for compact, stable URLs.
 */
export function applyMapViewToParams(searchParams, view) {
  const nextParams = new URLSearchParams(searchParams);
  nextParams.set(QueryParams.lat, view.lat.toFixed(COORDINATE_PRECISION));
  nextParams.set(QueryParams.lng, view.lng.toFixed(COORDINATE_PRECISION));
  nextParams.set(QueryParams.alt, String(Math.round(view.altitude)));
  nextParams.set(QueryParams.heading, String(Math.round(view.heading)));
  nextParams.set(QueryParams.tilt, String(Math.round(view.tilt)));
  return nextParams;
}

/**
 * Removes the world map camera pose (lat/lng/alt/heading/tilt) from the given query
 * params, preserving all other params. Used to return the URL to its pristine,
 * param-free state (e.g. on reset view).
 */
export function removeMapViewFromParams(searchParams) {
  const nextParams = new URLSearchParams(searchParams);
  nextParams.delete(QueryParams.lat);
  nextParams.delete(QueryParams.lng);
  nextParams.delete(QueryParams.alt);
  nextParams.delete(QueryParams.heading);
  nextParams.delete(QueryParams.tilt);
  return nextParams;
}
/**
 * Connects the projects world map to the URL via a deep link.
 * On mount it reads the `lat`, `lng`, `alt` (and optional `heading`/`tilt`) query params to restore the previous view,
 * and it writes them back whenever the map comes to rest so the position can be shared or restored.
 */
export function useProjectsMapDeepLink() {
  const $ = _c(10);
  const [searchParams, setSearchParams] = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = () => parseMapViewFromParams(searchParams);
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [initialView] = useState(t0);
  const skipNextViewStableRef = useRef(false);
  let t1;
  if ($[2] !== setSearchParams) {
    t1 = view => {
      if (skipNextViewStableRef.current) {
        skipNextViewStableRef.current = false;
        return;
      }
      setSearchParams(prev => applyMapViewToParams(prev, view), {
        replace: true
      });
    };
    $[2] = setSearchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onViewStable = t1;
  let t2;
  if ($[4] !== setSearchParams) {
    t2 = () => {
      skipNextViewStableRef.current = true;
      setSearchParams(_temp, {
        replace: true
      });
    };
    $[4] = setSearchParams;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const resetView = t2;
  let t3;
  if ($[6] !== initialView || $[7] !== onViewStable || $[8] !== resetView) {
    t3 = {
      initialView,
      onViewStable,
      resetView
    };
    $[6] = initialView;
    $[7] = onViewStable;
    $[8] = resetView;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function _temp(prev_0) {
  return removeMapViewFromParams(prev_0);
}